/**
 * Guide Media Upload Utility
 *
 * Helper functions for uploading media files to Firebase Storage
 * and managing Guide media assets.
 */

import { StorageService } from '../../firebase-core/services/StorageService';

// ============================================================================
// TYPES
// ============================================================================

export interface GuideContentItem {
  id?: string;
  title?: string;
  description?: string;
  subtitle?: string;
  fullArticleLink?: string;
  image?: string | File;
  video?: string | File;
}

export interface ProcessedGuideContentItem {
  id?: string;
  title?: string;
  description?: string;
  subtitle?: string;
  fullArticleLink?: string;
  image?: string;
  video?: string;
}

export interface UploadMediaResult {
  success: boolean;
  url?: string;
  error?: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const GUIDES_STORAGE_PATH = 'guides';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate a unique storage path for media file
 */
const generateMediaPath = (
  guideId: string,
  contentIndex: number,
  fileName: string,
  mediaType: 'image' | 'video'
): string => {
  const timestamp = Date.now();
  const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
  return `${GUIDES_STORAGE_PATH}/${guideId}/content-${contentIndex}/${mediaType}-${timestamp}-${sanitizedFileName}`;
};

/**
 * Get content type for file
 */
const getContentType = (file: File): string => {
  return file.type || 'application/octet-stream';
};

/**
 * Upload a single media file to Storage
 */
export const uploadMediaFile = async (
  file: File,
  guideId: string,
  contentIndex: number,
  mediaType: 'image' | 'video'
): Promise<UploadMediaResult> => {
  try {
    const storageService = new StorageService();
    const storagePath = generateMediaPath(guideId, contentIndex, file.name, mediaType);

    // Upload file
    await storageService.upload(storagePath, file, {
      contentType: getContentType(file),
    });

    // Get download URL
    const downloadURL = await storageService.getDownloadURL(storagePath);

    return {
      success: true,
      url: downloadURL,
    };
  } catch (error) {
    console.error('Error uploading media file:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

/**
 * Upload all media files in content items
 *
 * Processes content items and uploads any File objects to Storage,
 * replacing them with download URLs.
 *
 * @param contentItems - Array of content items with potential File objects
 * @param guideId - Guide ID for storage path
 * @returns Updated content items with URLs instead of File objects
 */
export const uploadGuideMedia = async (
  contentItems: GuideContentItem[],
  guideId: string
): Promise<ProcessedGuideContentItem[]> => {
  if (!contentItems || contentItems.length === 0) {
    return [];
  }

  const uploadPromises = contentItems.map(async (item, index): Promise<ProcessedGuideContentItem> => {
    const updatedItem: ProcessedGuideContentItem = {
      id: item.id,
      title: item.title,
      description: item.description,
      subtitle: item.subtitle,
      fullArticleLink: item.fullArticleLink,
      image: typeof item.image === 'string' ? item.image : undefined,
      video: typeof item.video === 'string' ? item.video : undefined,
    };

    // Upload image if it's a File
    if (item.image && item.image instanceof File) {
      const result = await uploadMediaFile(item.image, guideId, index, 'image');
      if (result.success && result.url) {
        updatedItem.image = result.url;
      } else {
        console.error(`Failed to upload image for content item ${index}:`, result.error);
        updatedItem.image = undefined;
      }
    }

    // Upload video if it's a File
    if (item.video && item.video instanceof File) {
      const result = await uploadMediaFile(item.video, guideId, index, 'video');
      if (result.success && result.url) {
        updatedItem.video = result.url;
      } else {
        console.error(`Failed to upload video for content item ${index}:`, result.error);
        updatedItem.video = undefined;
      }
    }

    return updatedItem;
  });

  return Promise.all(uploadPromises);
};

/**
 * Delete media file from Storage
 *
 * Useful for cleanup when removing content items or replacing media.
 */
export const deleteMediaFile = async (mediaUrl: string): Promise<boolean> => {
  try {
    const storageService = new StorageService();

    // Extract storage path from URL
    // Firebase Storage URLs have format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?...
    const urlObj = new URL(mediaUrl);
    const pathMatch = urlObj.pathname.match(/\/o\/(.+)/);

    if (!pathMatch) {
      console.error('Could not extract storage path from URL:', mediaUrl);
      return false;
    }

    const storagePath = decodeURIComponent(pathMatch[1]);
    await storageService.delete(storagePath);

    return true;
  } catch (error) {
    console.error('Error deleting media file:', error);
    return false;
  }
};

/**
 * Check if value is a File object
 */
export const isFileObject = (value: any): value is File => {
  return value instanceof File;
};

/**
 * Check if content items contain any File objects that need upload
 */
export const hasFilesToUpload = (contentItems: GuideContentItem[]): boolean => {
  return contentItems.some(
    item => isFileObject(item.image) || isFileObject(item.video)
  );
};
